import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Upload, AlertCircle, CheckCircle, RefreshCw, Film, Image, Trash2, X, Play, Volume2, VolumeX, ToggleLeft, ToggleRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Max file size: 500MB
const MAX_VIDEO_SIZE = 500 * 1024 * 1024;
const MAX_POSTER_SIZE = 5 * 1024 * 1024;
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

/**
 * Sleep helper for retry delays
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Multipart upload handler for large video files with retry logic
 */
const useMultipartUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentPart, setCurrentPart] = useState(0);
  const [totalParts, setTotalParts] = useState(0);
  const [uploadPhase, setUploadPhase] = useState(''); // 'init', 'uploading', 'completing', 'complete'
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const uploadSessionRef = useRef(null);

  const uploadVideo = useCallback(async (file, slot, onSuccess) => {
    if (uploading) return;
    
    setUploading(true);
    setProgress(0);
    setCurrentPart(0);
    setTotalParts(0);
    setUploadPhase('init');
    setError(null);
    
    abortControllerRef.current = new AbortController();
    
    console.log(`[MEMORIAL-UPLOAD] Starting multipart upload: ${file.name} (${(file.size / (1024*1024)).toFixed(1)}MB) to ${slot}`);
    
    try {
      // Step 1: Initialize multipart upload
      console.log('[MEMORIAL-UPLOAD] Phase 1: Initializing multipart upload...');
      const initRes = await axios.post(`${API}/memorial-videos/multipart/init`, {
        slot,
        filename: file.name,
        fileSize: file.size,
        contentType: file.type || 'video/mp4'
      });
      
      if (!initRes.data.success) {
        throw new Error(initRes.data.detail || 'Failed to initialize upload');
      }
      
      const { uploadId, objectKey, partUrls, partSize, numParts, useProxy } = initRes.data;
      uploadSessionRef.current = { uploadId, objectKey, slot };
      setTotalParts(numParts);
      
      console.log(`[MEMORIAL-UPLOAD] Initialized: uploadId=${uploadId.substring(0, 20)}..., ${numParts} parts of ${(partSize/(1024*1024)).toFixed(1)}MB, useProxy=${useProxy}`);
      
      // Step 2: Upload parts with retry logic
      setUploadPhase('uploading');
      const uploadedParts = [];
      let uploadedBytes = 0;
      
      for (let i = 0; i < numParts; i++) {
        // Check if aborted
        if (abortControllerRef.current?.signal.aborted) {
          throw new Error('Upload cancelled by user');
        }
        
        const partNum = i + 1;
        setCurrentPart(partNum);
        const start = i * partSize;
        const end = Math.min(start + partSize, file.size);
        const chunk = file.slice(start, end);
        
        let etag = null;
        let lastError = null;
        
        // Retry loop for this part
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          // Check if aborted between retries
          if (abortControllerRef.current?.signal.aborted) {
            throw new Error('Upload cancelled by user');
          }
          
          try {
            console.log(`[MEMORIAL-UPLOAD] Uploading part ${partNum}/${numParts} (${(chunk.size/(1024*1024)).toFixed(1)}MB) - attempt ${attempt}/${MAX_RETRIES}`);
            
            if (useProxy) {
              // Upload through our backend proxy (avoids CORS issues)
              const proxyRes = await axios.put(
                `${API}/memorial-videos/multipart/upload-part/${uploadId}/${partNum}`,
                chunk,
                {
                  headers: { 'Content-Type': 'application/octet-stream' },
                  signal: abortControllerRef.current.signal,
                  maxBodyLength: Infinity,
                  maxContentLength: Infinity
                }
              );
              
              if (!proxyRes.data.success) {
                throw new Error(proxyRes.data.detail || `Part ${partNum} upload failed`);
              }
              
              etag = proxyRes.data.ETag;
            } else {
              // Direct upload to R2 using presigned URL
              const partUrl = partUrls[i].url;
              const uploadRes = await fetch(partUrl, {
                method: 'PUT',
                body: chunk,
                signal: abortControllerRef.current.signal
              });
              
              if (!uploadRes.ok) {
                const errorText = await uploadRes.text().catch(() => '');
                throw new Error(`HTTP ${uploadRes.status}${errorText ? ` - ${errorText}` : ''}`);
              }
              
              etag = uploadRes.headers.get('ETag');
              if (etag) etag = etag.replace(/"/g, '');
            }
            
            if (!etag) {
              throw new Error('No ETag returned from server');
            }
            
            console.log(`[MEMORIAL-UPLOAD] Part ${partNum} complete: ETag=${etag.substring(0, 16)}...`);
            break; // Success, exit retry loop
            
          } catch (partError) {
            lastError = partError;
            
            if (partError.name === 'CanceledError' || partError.message?.includes('cancelled')) {
              throw new Error('Upload cancelled by user');
            }
            
            if (attempt < MAX_RETRIES) {
              console.warn(`[MEMORIAL-UPLOAD] Part ${partNum} failed (attempt ${attempt}/${MAX_RETRIES}): ${partError.message}. Retrying in ${RETRY_DELAY_MS}ms...`);
              await sleep(RETRY_DELAY_MS * attempt); // Exponential backoff
            }
          }
        }
        
        // If still no etag after all retries, fail
        if (!etag) {
          throw new Error(`Failed to upload part ${partNum} after ${MAX_RETRIES} attempts: ${lastError?.message || 'Unknown error'}`);
        }
        
        uploadedParts.push({ PartNumber: partNum, ETag: etag });
        
        // Update progress
        uploadedBytes += chunk.size;
        setProgress(Math.round((uploadedBytes / file.size) * 95)); // Reserve 5% for completion
      }
      
      // Step 3: Complete multipart upload
      console.log('[MEMORIAL-UPLOAD] Phase 3: Completing multipart upload...');
      setUploadPhase('completing');
      setProgress(97);
      
      const completeRes = await axios.post(`${API}/memorial-videos/multipart/complete`, {
        uploadId,
        objectKey,
        slot,
        parts: uploadedParts
      });
      
      if (!completeRes.data.success) {
        throw new Error(completeRes.data.detail || 'Failed to complete upload');
      }
      
      uploadSessionRef.current = null;
      setProgress(100);
      setUploadPhase('complete');
      
      console.log(`[MEMORIAL-UPLOAD] SUCCESS - Video uploaded to: ${objectKey}`);
      
      // Show complete briefly then call success callback
      setTimeout(() => {
        setUploadPhase('');
        if (onSuccess) onSuccess(objectKey);
      }, 1500);
      
      return objectKey;
      
    } catch (err) {
      console.error('[MEMORIAL-UPLOAD] FAILED:', err);
      
      if (err.message?.includes('cancelled') || err.name === 'AbortError') {
        // Try to abort on server side
        if (uploadSessionRef.current) {
          console.log('[MEMORIAL-UPLOAD] Cleaning up cancelled upload on server...');
          try {
            await axios.post(`${API}/memorial-videos/multipart/abort`, {
              uploadId: uploadSessionRef.current.uploadId,
              objectKey: uploadSessionRef.current.objectKey
            });
            console.log('[MEMORIAL-UPLOAD] Server cleanup successful');
          } catch (abortErr) {
            console.error('[MEMORIAL-UPLOAD] Server cleanup failed:', abortErr);
          }
        }
        setError('Upload cancelled');
      } else {
        const errorMsg = err.response?.data?.detail || err.message || 'Upload failed - check console for details';
        setError(errorMsg);
      }
      throw err;
    } finally {
      setUploading(false);
      abortControllerRef.current = null;
    }
  }, [uploading]);

  const abort = useCallback(async () => {
    console.log('[MEMORIAL-UPLOAD] User requested abort');
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return { uploadVideo, abort, uploading, progress, error, setError, currentPart, totalParts, uploadPhase };
};


/**
 * Delete confirmation modal
 */
const DeleteConfirmModal = ({ isOpen, slot, itemType, onConfirm, onCancel, deleting }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-zinc-800 rounded-xl p-6 max-w-md w-full mx-4 border border-zinc-700">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-500/20 rounded-full">
            <Trash2 className="w-6 h-6 text-red-400" />
          </div>
          <h3 className="text-lg font-semibold">Delete {slot} {itemType}?</h3>
        </div>
        <p className="text-zinc-400 mb-6">
          This will remove the {itemType.toLowerCase()} from the live landing page. This action cannot be undone.
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center gap-2 transition disabled:opacity-50"
            data-testid={`confirm-delete-memorial-${slot}-${itemType.toLowerCase()}-btn`}
          >
            {deleting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};


/**
 * Mini video preview player for admin
 * - ALWAYS starts muted with large sound overlay button
 * - First click on sound button restarts from 0:00 with audio
 * - After first enable, button shrinks to normal mute toggle
 */
const VideoPreview = ({ slot }) => {
  const videoRef = useRef(null);
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  // Per-player flag: has audio been enabled at least once?
  const hasStartedWithSoundRef = useRef(false);
  // Track previous URL to detect src changes
  const prevUrlRef = useRef(null);
  
  useEffect(() => {
    const fetchUrl = async () => {
      try {
        const res = await axios.get(`${API}/memorial-videos/playback/${slot}`);
        if (res.data.hasVideo && res.data.url) {
          setUrl(res.data.url);
        }
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchUrl();
  }, [slot]);

  /**
   * Force muted state on mount and whenever src changes
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
    // Detect src change - reset the sound flag
    if (prevUrlRef.current !== url && url) {
      console.log(`[MEMORIAL-ADMIN-${slot}] URL changed - resetting sound state`);
      hasStartedWithSoundRef.current = false;
      setIsMuted(true);
    }
    prevUrlRef.current = url;
    
    // ALWAYS ensure video starts muted
    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;
  }, [url, slot]);
  
  /**
   * First-interaction audio restart logic - ONLY triggered by overlay button
   */
  const handleSoundButtonClick = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    
    if (!hasStartedWithSoundRef.current) {
      console.log(`[MEMORIAL-ADMIN-${slot}] First sound enable - restarting from 0:00`);
      video.currentTime = 0;
      video.muted = false;
      video.volume = 1;
      video.play().catch((err) => {
        console.log(`[MEMORIAL-ADMIN-${slot}] Play after unmute blocked:`, err.message);
      });
      hasStartedWithSoundRef.current = true;
      setIsMuted(false);
    } else {
      // Already started with sound before - just toggle mute normally (no restart)
      video.muted = !video.muted;
      video.volume = video.muted ? 0 : 1;
      setIsMuted(video.muted);
    }
  }, [slot]);
  
  if (loading) {
    return (
      <div className="aspect-video bg-zinc-800 rounded-lg flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }
  
  if (error || !url) {
    return (
      <div className="aspect-video bg-zinc-800 rounded-lg flex items-center justify-center">
        <span className="text-zinc-500 text-sm">Preview unavailable</span>
      </div>
    );
  }
  
  return (
    <div className="relative group">
      <video
        ref={videoRef}
        src={url}
        className="w-full aspect-video rounded-lg bg-black object-cover"
        controls
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        muted
        preload="metadata"
        data-testid={`memorial-admin-${slot}-preview`}
      />
      {/* Sound Overlay Button - LARGE when muted (first time), small after first enable */}
      <button
        onClick={handleSoundButtonClick}
        className={`absolute z-10 transition-all duration-300 ${
          isMuted && !hasStartedWithSoundRef.current
            ? 'top-2 right-2 px-3 py-2 bg-black/70 hover:bg-black/90 rounded-lg backdrop-blur-sm flex items-center gap-2 border border-white/20'
            : 'top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full backdrop-blur-sm'
        }`}
        title={isMuted ? "Tap for sound" : "Mute"}
        data-testid={`memorial-admin-${slot}-sound-toggle`}
      >
        {isMuted ? (
          <>
            <VolumeX className={`${!hasStartedWithSoundRef.current ? 'w-5 h-5' : 'w-4 h-4'} text-white`} />
            {!hasStartedWithSoundRef.current && (
              <span className="text-white font-medium text-xs">Sound</span>
            )}
          </>
        ) : (
          <Volume2 className="w-4 h-4 text-purple-400" />
        )}
      </button>
    </div>
  );
};


/**
 * Video slot component with upload, preview, and delete
 */
const VideoSlot = ({ slot, label, description, config, onRefresh }) => {
  const fileInputRef = useRef(null);
  const posterInputRef = useRef(null);
  const { uploadVideo, abort, uploading, progress, error, setError, currentPart, totalParts, uploadPhase } = useMultipartUpload();
  const [posterUploading, setPosterUploading] = useState(false);
  const [deleteVideoModalOpen, setDeleteVideoModalOpen] = useState(false);
  const [deletePosterModalOpen, setDeletePosterModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [success, setSuccess] = useState(null);
  
  const videoKey = config?.[`${slot}VideoKey`];
  const posterKey = config?.[`${slot}PosterKey`];
  const hasVideo = !!videoKey;
  const hasPoster = !!posterKey;
  
  // Extract filename from key
  const getFilenameFromKey = (key) => {
    if (!key) return null;
    const parts = key.split('/');
    return parts[parts.length - 1];
  };
  
  // Generate upload phase label
  const getPhaseLabel = () => {
    switch (uploadPhase) {
      case 'init': return 'Initializing upload...';
      case 'uploading': return `Uploading part ${currentPart} of ${totalParts}`;
      case 'completing': return 'Finalizing upload...';
      case 'complete': return 'Upload complete!';
      default: return 'Uploading...';
    }
  };
  
  // Handle video file selection
  const handleVideoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate size
    if (file.size > MAX_VIDEO_SIZE) {
      setError(`Video too large (${(file.size / (1024*1024)).toFixed(1)}MB). Maximum: ${MAX_VIDEO_SIZE / (1024 * 1024)}MB`);
      return;
    }
    
    // Validate type
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!allowedTypes.includes(file.type)) {
      setError(`Invalid video format "${file.type}". Use MP4, WebM, or MOV.`);
      return;
    }
    
    console.log(`[UI] Selected file: ${file.name} (${(file.size/(1024*1024)).toFixed(1)}MB, ${file.type})`);
    
    try {
      await uploadVideo(file, slot, () => {
        setSuccess('Video uploaded successfully!');
        setTimeout(() => setSuccess(null), 3000);
        onRefresh();
      });
    } catch (err) {
      // Error already handled in hook
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Handle poster upload
  const handlePosterSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > MAX_POSTER_SIZE) {
      setError(`Poster too large (${(file.size / (1024*1024)).toFixed(1)}MB). Maximum: ${MAX_POSTER_SIZE / (1024 * 1024)}MB`);
      return;
    }
    
    setPosterUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/memorial-videos/poster/${slot}`, formData);
      setSuccess('Poster uploaded!');
      setTimeout(() => setSuccess(null), 3000);
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Poster upload failed');
    } finally {
      setPosterUploading(false);
      if (posterInputRef.current) {
        posterInputRef.current.value = '';
      }
    }
  };
  
  // Handle video delete
  const handleDeleteVideo = async () => {
    setDeleting(true);
    console.log(`[UI] Deleting ${slot} video...`);
    try {
      const res = await axios.delete(`${API}/memorial-videos/media/${slot}`);
      setDeleteVideoModalOpen(false);
      if (res.data.errors?.length > 0) {
        setError(`Deleted with warnings: ${res.data.errors.join(', ')}`);
      } else {
        setSuccess('Video deleted successfully');
      }
      setTimeout(() => setSuccess(null), 3000);
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };
  
  // Handle poster delete
  const handleDeletePoster = async () => {
    setDeleting(true);
    console.log(`[UI] Deleting ${slot} poster...`);
    try {
      await axios.delete(`${API}/memorial-videos/poster/${slot}`);
      setDeletePosterModalOpen(false);
      setSuccess('Poster deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Poster delete failed');
    } finally {
      setDeleting(false);
    }
  };
  
  const iconColor = slot === 'hero' ? 'text-purple-400' : 'text-indigo-400';
  
  return (
    <div className="card-dark p-6 border border-zinc-700">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Film size={18} className={iconColor} />
          <h3 className="font-semibold">{label}</h3>
        </div>
      </div>
      
      <p className="text-sm text-zinc-400 mb-4">{description}</p>
      
      {/* Status Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg flex items-start gap-2">
          <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={16} />
          <span className="text-red-400 text-sm flex-1">{error}</span>
          <button onClick={() => setError(null)} className="text-zinc-400 hover:text-white flex-shrink-0">
            <X size={14} />
          </button>
        </div>
      )}
      
      {success && (
        <div className="mb-4 p-3 bg-green-500/20 border border-green-500/30 rounded-lg flex items-center gap-2">
          <CheckCircle className="text-green-400" size={16} />
          <span className="text-green-400 text-sm">{success}</span>
        </div>
      )}
      
      {/* Current Video Status */}
      {hasVideo && !uploading && (
        <div className="mb-4 p-4 bg-zinc-800/50 border border-zinc-700 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Play size={16} className="text-green-400" />
            <span className="text-green-400 font-medium text-sm">Current video active</span>
          </div>
          <div className="mb-3">
            <VideoPreview slot={slot} />
          </div>
          <div className="text-xs text-zinc-500 space-y-1">
            <p>File: {getFilenameFromKey(videoKey)}</p>
            {config?.updatedAt && <p>Updated: {new Date(config.updatedAt).toLocaleString()}</p>}
          </div>
          <button
            onClick={() => setDeleteVideoModalOpen(true)}
            disabled={deleting}
            className="mt-3 px-3 py-1.5 text-sm bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-2 transition disabled:opacity-50"
            data-testid={`delete-memorial-${slot}-video-btn`}
          >
            <Trash2 size={14} /> Delete Video
          </button>
        </div>
      )}
      
      <div className="grid md:grid-cols-2 gap-6">
        {/* Video Upload */}
        <div>
          <label className="block text-sm text-zinc-400 mb-2">Video File (MP4, up to 500MB)</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,video/quicktime"
            onChange={handleVideoSelect}
            className="hidden"
            disabled={uploading}
            id={`memorial-${slot}-video-input`}
          />
          
          {uploading ? (
            <div className="p-4 border-2 border-dashed border-purple-500/50 rounded-lg bg-purple-500/5">
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-medium ${uploadPhase === 'complete' ? 'text-green-400' : 'text-purple-400'}`}>
                  {getPhaseLabel()}
                </span>
                {uploadPhase !== 'complete' && (
                  <button 
                    onClick={abort}
                    className="px-3 py-1 text-sm bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded flex items-center gap-1 transition"
                    title="Cancel upload"
                  >
                    <X size={14} /> Cancel
                  </button>
                )}
              </div>
              <div className="w-full bg-zinc-700 rounded-full h-3 overflow-hidden">
                <div 
                  className={`h-3 rounded-full transition-all duration-300 ease-out ${uploadPhase === 'complete' ? 'bg-green-500' : 'bg-gradient-to-r from-purple-500 to-purple-400'}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-zinc-400">{progress}% complete</span>
              </div>
            </div>
          ) : (
            <label 
              htmlFor={`memorial-${slot}-video-input`}
              className="flex items-center justify-center gap-2 w-full p-4 border-2 border-dashed border-zinc-600 hover:border-purple-500 rounded-lg cursor-pointer transition"
            >
              <Upload size={20} />
              <span>{hasVideo ? 'Replace Video' : 'Upload Video'}</span>
            </label>
          )}
        </div>
        
        {/* Poster Upload */}
        <div>
          <label className="block text-sm text-zinc-400 mb-2">Poster Image (shown before video loads)</label>
          <input
            ref={posterInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handlePosterSelect}
            className="hidden"
            disabled={posterUploading || uploading}
            id={`memorial-${slot}-poster-input`}
          />
          <label 
            htmlFor={`memorial-${slot}-poster-input`}
            className={`flex items-center justify-center gap-2 w-full p-4 border-2 border-dashed border-zinc-600 hover:border-purple-500 rounded-lg cursor-pointer transition ${(posterUploading || uploading) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {posterUploading ? (
              <RefreshCw size={20} className="animate-spin" />
            ) : (
              <Image size={20} />
            )}
            <span>{hasPoster ? 'Replace Poster' : 'Upload Poster'}</span>
          </label>
          
          {hasPoster && (
            <div className="mt-3 flex items-center justify-between">
              <span className="text-sm text-green-400">✓ Poster uploaded</span>
              <button
                onClick={() => setDeletePosterModalOpen(true)}
                disabled={deleting || uploading}
                className="text-xs text-red-400 hover:text-red-300"
              >
                Remove Poster
              </button>
            </div>
          )}
          {!hasPoster && (
            <div className="mt-3 text-sm text-zinc-500">No poster set</div>
          )}
        </div>
      </div>
      
      {/* Delete Confirmation Modals */}
      <DeleteConfirmModal
        isOpen={deleteVideoModalOpen}
        slot={slot}
        itemType="Video"
        onConfirm={handleDeleteVideo}
        onCancel={() => setDeleteVideoModalOpen(false)}
        deleting={deleting}
      />
      <DeleteConfirmModal
        isOpen={deletePosterModalOpen}
        slot={slot}
        itemType="Poster"
        onConfirm={handleDeletePoster}
        onCancel={() => setDeletePosterModalOpen(false)}
        deleting={deleting}
      />
    </div>
  );
};


/**
 * Main Memorial Video Manager component for Admin panel
 */
const MemorialVideoManager = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toggles, setToggles] = useState({ enableTopVideo: true, enableBottomVideo: true });
  const [toggling, setToggling] = useState(false);
  
  const fetchConfig = async () => {
    try {
      setLoading(true);
      const [configRes, settingsRes] = await Promise.all([
        axios.get(`${API}/memorial-videos/admin`),
        axios.get(`${API}/memorial-settings`)
      ]);
      setConfig(configRes.data);
      const s = settingsRes.data || {};
      setToggles({
        enableTopVideo: s.enableTopVideo !== false && s.enableHeroVideo !== false,
        enableBottomVideo: s.enableBottomVideo !== false,
      });
      setError(null);
    } catch (err) {
      console.error('Failed to fetch Memorial video config:', err);
      setError(err.response?.data?.detail || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (field) => {
    setToggling(true);
    try {
      const res = await axios.put(`${API}/memorial-settings/admin`, { [field]: !toggles[field] });
      const s = res.data || {};
      setToggles({
        enableTopVideo: s.enableTopVideo !== false && s.enableHeroVideo !== false,
        enableBottomVideo: s.enableBottomVideo !== false,
      });
    } catch (err) {
      console.error('Toggle failed:', err);
    } finally {
      setToggling(false);
    }
  };
  
  useEffect(() => {
    fetchConfig();
  }, []);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-purple-400" />
        <span className="ml-2 text-zinc-400">Loading configuration...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-6 bg-red-500/10 border border-red-500/30 rounded-xl">
        <div className="flex items-center gap-2 text-red-400 mb-2">
          <AlertCircle size={18} />
          <span className="font-medium">Error loading configuration</span>
        </div>
        <p className="text-zinc-400 text-sm mb-4">{error}</p>
        <button
          onClick={fetchConfig}
          className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg flex items-center gap-2 transition"
        >
          <RefreshCw size={16} /> Retry
        </button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="memorial-video-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Film className="text-purple-400" />
            Memorial Page Videos
          </h2>
          <p className="text-sm text-zinc-400 mt-1">
            Storage: {config?.storageBackend === 's3' ? `Cloudflare R2 (${config?.storageBucket})` : 'Local'}
          </p>
        </div>
        <button
          onClick={fetchConfig}
          className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg flex items-center gap-2 transition"
        >
          <RefreshCw size={16} /> Refresh
        </button>
      </div>
      
      {/* Video Slots */}
      <div className="space-y-6">
        {/* Hero Video Toggle + Slot */}
        <div>
          <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-t-lg border border-zinc-700 border-b-0" data-testid="toggle-enableTopVideo">
            <div>
              <p className="text-white font-medium">Show Hero Video (Top of Page)</p>
              <p className="text-zinc-500 text-xs mt-0.5">Toggle OFF to completely remove this section from the public Memorial page</p>
            </div>
            <button onClick={() => handleToggle('enableTopVideo')} disabled={toggling}
              className={`transition-all duration-200 ${toggling ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              data-testid="toggle-btn-enableTopVideo">
              {toggles.enableTopVideo
                ? <ToggleRight className="w-10 h-10 text-green-400" />
                : <ToggleLeft className="w-10 h-10 text-zinc-500" />}
            </button>
          </div>
          <VideoSlot slot="hero" label="Hero Video (Top of Page)"
            description="Main hero video displayed at the top of the Memorial page. Autoplays muted with sound toggle."
            config={config} onRefresh={fetchConfig} />
        </div>

        {/* Demo Video Toggle + Slot */}
        <div>
          <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-t-lg border border-zinc-700 border-b-0" data-testid="toggle-enableBottomVideo">
            <div>
              <p className="text-white font-medium">Show Demo Video (Bottom of Page)</p>
              <p className="text-zinc-500 text-xs mt-0.5">Toggle OFF to completely remove this section from the public Memorial page</p>
            </div>
            <button onClick={() => handleToggle('enableBottomVideo')} disabled={toggling}
              className={`transition-all duration-200 ${toggling ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              data-testid="toggle-btn-enableBottomVideo">
              {toggles.enableBottomVideo
                ? <ToggleRight className="w-10 h-10 text-green-400" />
                : <ToggleLeft className="w-10 h-10 text-zinc-500" />}
            </button>
          </div>
          <VideoSlot slot="demo" label="Demo Video (Bottom of Page)"
            description="Demo/showcase video displayed further down the page. Autoplays muted when scrolled into view."
            config={config} onRefresh={fetchConfig} />
        </div>
      </div>
      
      {/* Last Updated Info */}
      {config?.updatedAt && (
        <div className="text-sm text-zinc-500 text-right">
          Last updated: {new Date(config.updatedAt).toLocaleString()} by {config.updatedBy || 'Unknown'}
        </div>
      )}
    </div>
  );
};

export default MemorialVideoManager;
